import React, { useEffect, useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Checkbox, ListItemText, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Paper, Card, CardContent, Grid, Divider, Stack } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { DataGrid, GridToolbar, GridColDef } from '@mui/x-data-grid';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { apiUrl } from '../utils/api';
import EmptyState from '../components/EmptyState';

// Colors used for pie chart slices. A light gray is used for the "NA" or
// "Not Affected" status in the charts.
const COLORS = ['#2f4ae1', '#12b59f', '#94a3b8'];
// Palette for the design-change impact pie chart. Colors will repeat if there
// are more slices than provided here.
const IMPACT_COLORS = [
  '#2f4ae1',
  '#12b59f',
  '#f59e0b',
  '#ef4444',
  '#6366f1',
  '#0ea5e9',
  '#f97316',
  '#22c55e',
  '#e11d48',
  '#3b82f6'
];

interface DashboardProps {
  projectId: number | null;
  access?: string;
}

interface MappingType {
  [key: string]: string;
}

const Dashboard: React.FC<DashboardProps> = ({ projectId, access = 'none' }) => {
  const [designChanges, setDesignChanges] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [assignStatusFilter, setAssignStatusFilter] = useState<string[]>([]);
  const [changeFilter, setChangeFilter] = useState<string[]>([]);
  const designNumFilter = createFilterOptions<string>({
    matchFrom: 'any',
    stringify: (option: string) => option
  });

  // third-party export states
  const [confirmThirdParty, setConfirmThirdParty] = useState<boolean>(false);
  const [pendingExport, setPendingExport] = useState<(() => void) | null>(null);
  const [mappingOpen, setMappingOpen] = useState<boolean>(false);
  const [mapFile, setMapFile] = useState<File | null>(null);
  const mappingFields = [
    'document_number',
    'document_name',
    'document_location',
    'document_category',
    'document_revision',
    'document_status',
    'issue_date',
    'resubmission_reason'
  ];
  const [mapping, setMapping] = useState<MappingType>(
    mappingFields.reduce((acc, f) => ({ ...acc, [f]: '' }), {})
  );
  const [dateFormat, setDateFormat] = useState<string>('');


  // Fetch design changes
  useEffect(() => {
    if (access === 'none' || !projectId) {
      setDesignChanges([]);
      return;
    }
    fetch(apiUrl(`/api/design-changes?project_id=${projectId}`))
      .then(res => res.json())
      .then(setDesignChanges)
      .catch(console.error);
  }, [projectId, access]);

  // Fetch documents
  useEffect(() => {
    if (access === 'none' || !projectId) {
      setDocuments([]);
      return;
    }
    fetch(apiUrl(`/api/documents?project_id=${projectId}`))
      .then(res => res.json())
      .then(data => setDocuments(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, [projectId, access]);

  // Fetch assignments
  useEffect(() => {
    if (access === 'none' || !projectId) {
      setAssignments([]);
      return;
    }
    fetch(apiUrl(`/api/assignments?project_id=${projectId}`))
      .then(res => res.json())
      .then(data => setAssignments(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, [projectId, access]);

  if (access === 'none') {
    return <div>Access denied</div>;
  }

  // Chart data for design changes
  const designChartData = [
    { name: 'Completed', value: designChanges.filter(d => d.change_status === 'Completed').length },
    { name: 'Pending', value: designChanges.filter(d => d.change_status === 'Pending').length },
    { name: 'NA', value: designChanges.filter(d => d.change_status === 'NA').length }
  ];
  const designTotal = designChanges.length;
  const designLegendData = designChartData.map((entry, idx) => ({
    color: COLORS[idx % COLORS.length],
    value: entry.name,
    payload: entry
  }));

  // Chart data for documents. These values should respect the current
  // filters applied to the documents table, so calculations are based on
  // the filteredDocuments array defined later in this file.
  let documentChartData = [];
  let documentLegendData = [];
  let documentTotal = 0;

  // Add document counts to each design change
  const designChangesWithCounts = designChanges.map(dc => {
    const assigns = assignments.filter(a => a.change_number === dc.change_number);
    const totalDocuments = new Set(assigns.map(a => a.document_number)).size;
    const pendingDocuments = new Set(
      assigns
        .filter(a => a.assignment_document_status === 'Pending')
        .map(a => a.document_number)
    ).size;
    return {
      ...dc,
      total_documents: totalDocuments,
      pending_documents: pendingDocuments
    };
  });

  // Filters applied
  const filteredDesign = designChangesWithCounts
    .filter(d => statusFilter.length === 0 || statusFilter.includes(d.change_status));

  const documentsWithAssigns = documents.map(d => {
    const assigns = assignments.filter(a => a.document_number === d.document_number);
    const pending = assigns
      .filter(a => a.assignment_document_status === 'Pending')
      .map(a => a.change_number);
    const submitted = assigns
      .filter(a => a.assignment_document_status === 'Submitted')
      .map(a => a.change_number);
    return {
      ...d,
      assigned_changes: assigns.map(a => a.change_number),
      pending_changes: pending,
      submitted_changes: submitted,
      assignments: assigns
    };
  });

  const filteredDocuments = documentsWithAssigns
    .filter(d =>
      locationFilter.length === 0 || locationFilter.includes(d.document_location)
    )
    .filter(d =>
      changeFilter.length === 0 ||
      d.assigned_changes.some(c => changeFilter.includes(c))
    )
    .filter(d => {
      if (assignStatusFilter.length === 0) return true;
      const notAffectedSelected = assignStatusFilter.includes('Not Affected');
      const statuses = assignStatusFilter.filter(s => s !== 'Not Affected');
      const matchStatus =
        statuses.length === 0
          ? false
          : d.assignments.some(a => statuses.includes(a.assignment_document_status));
      const matchNotAffected = notAffectedSelected ? d.assignments.length === 0 : false;
      if (notAffectedSelected && statuses.length > 0) {
        return matchStatus || matchNotAffected;
      }
      if (notAffectedSelected) return matchNotAffected;
      return matchStatus;
    });

  // Recalculate document pie chart data based on the currently filtered
  // documents list. Each document is counted once for each status it appears
  // in. The "Not Affected" category represents documents without any
  // assignments.
  const submittedCount = filteredDocuments.filter(d =>
    d.assignments.some(a => a.assignment_document_status === 'Submitted')
  ).length;
  const pendingCount = filteredDocuments.filter(d =>
    d.assignments.some(a => a.assignment_document_status === 'Pending')
  ).length;
  const notAffectedCount = filteredDocuments.filter(d => d.assignments.length === 0).length;

  documentChartData = [
    { name: 'Submitted', value: submittedCount },
    { name: 'Pending', value: pendingCount },
    { name: 'Not Affected', value: notAffectedCount }
  ];
  documentTotal = filteredDocuments.length;
  documentLegendData = documentChartData.map((entry, idx) => ({
    color: COLORS[idx % COLORS.length],
    value: entry.name,
    payload: entry
  }));

  // Helper used for filter calculations
  const matchesStatus = (d, statuses) => {
    if (statuses.length === 0) return true;
    const notAffectedSelected = statuses.includes('Not Affected');
    const list = statuses.filter(s => s !== 'Not Affected');
    const matchStatus =
      list.length === 0
        ? false
        : d.assignments.some(a => list.includes(a.assignment_document_status));
    const matchNotAffected = notAffectedSelected ? d.assignments.length === 0 : false;
    if (notAffectedSelected && list.length > 0) {
      return matchStatus || matchNotAffected;
    }
    if (notAffectedSelected) return matchNotAffected;
    return matchStatus;
  };

  // Dynamic dropdown options based on other filters
  const documentsForChangeOptions = documentsWithAssigns
    .filter(d =>
      locationFilter.length === 0 || locationFilter.includes(d.document_location)
    )
    .filter(d => matchesStatus(d, assignStatusFilter));

  const availableChangeNums = new Set(
    documentsForChangeOptions.flatMap(d => d.assigned_changes)
  );
  const allDesignNums = Array.from(
    new Set(designChanges.map(dc => dc.change_number))
  ).filter(n => n);
  const designNumbers =
    locationFilter.length === 0 && assignStatusFilter.length === 0
      ? allDesignNums
      : allDesignNums.filter(num => availableChangeNums.has(num));

  const documentsForLocationOptions = documentsWithAssigns
    .filter(d => matchesStatus(d, assignStatusFilter))
    .filter(d =>
      changeFilter.length === 0 || d.assigned_changes.some(c => changeFilter.includes(c))
    );
  const locations = Array.from(
    new Set(documentsForLocationOptions.map(d => d.document_location))
  ).filter(l => l);

  const documentsForStatusOptions = documentsWithAssigns
    .filter(d =>
      locationFilter.length === 0 || locationFilter.includes(d.document_location)
    )
    .filter(d =>
      changeFilter.length === 0 || d.assigned_changes.some(c => changeFilter.includes(c))
    );
  const statusSet = new Set();
  documentsForStatusOptions.forEach(d => {
    if (d.assignments.length === 0) statusSet.add('Not Affected');
    d.assignments.forEach(a => a.assignment_document_status && statusSet.add(a.assignment_document_status));
  });
  const statusOptions = ['Pending', 'Submitted', 'Not Affected'].filter(s => statusSet.has(s));

  // Pie data showing document counts for each design change. This respects the
  // current document table filters. By default all design changes are included,
  // even those with status "NA". If the documents table is filtered to show
  // pending assignments, completed design changes should not appear in this
  // chart.
  const hideCompleted = assignStatusFilter.includes('Pending');
  const impactChartData = designChanges
    .filter(dc => !hideCompleted || dc.change_status !== 'Completed')
    .map(dc => {
      const count = filteredDocuments.filter(d =>
        (d.assigned_changes || []).includes(dc.change_number)
      ).length;
      return { name: dc.change_number, value: count };
    })
    .filter(entry => entry.value > 0);

  const impactLegendData = impactChartData.map((entry, idx) => ({
    color: IMPACT_COLORS[idx % IMPACT_COLORS.length],
    value: entry.name,
    payload: entry
  }));

  // Handlers
  const handleDesignPieClick = (entry) =>
    setStatusFilter(prev =>
      prev.includes(entry.name)
        ? prev.filter(s => s !== entry.name)
        : [...prev, entry.name]
    );
  const handleImpactPieClick = (entry) =>
    setChangeFilter(prev =>
      prev.includes(entry.name)
        ? prev.filter(c => c !== entry.name)
        : [...prev, entry.name]
    );
  const clearDesignFilter = () => setStatusFilter([]);
  const clearLocationFilter = () => setLocationFilter([]);
  const clearAssignFilter = () => setAssignStatusFilter([]);
  const clearChangeFilter = () => setChangeFilter([]);
  const clearAllDocumentFilters = () => {
    setAssignStatusFilter([]);
    setChangeFilter([]);
    setLocationFilter([]);
  };

  // Columns for DataGrid
  const designCols: GridColDef[] = [
    { field: 'change_number', headerName: 'Number', flex: 1 },
    { field: 'change_name', headerName: 'Name', flex: 2 },
    { field: 'change_comments', headerName: 'Comments', flex: 3 },
    { field: 'change_date_issued', headerName: 'Date Issued', flex: 1 },
    { field: 'change_status', headerName: 'Status', flex: 1 },
    { field: 'total_documents', headerName: 'Total DOC', flex: 1, type: 'number' as const },
    { field: 'pending_documents', headerName: 'Pending DOC', flex: 1, type: 'number' as const }
  ];

  const renderDesignLegend = ({ payload }: any) => (
    <Box sx={{ ml: 2 }}>
      {payload.map((entry: any, idx: number) => (
        <Typography key={`item-${idx}`} variant="body2">
          <span style={{ color: entry.color, marginRight: 4 }}>&#9632;</span>
          {`${entry.value}: ${entry.payload.value}`}
        </Typography>
      ))}
      <Typography variant="body2">{`Total: ${designTotal}`}</Typography>
    </Box>
  );

  const documentCols = [
    { field: 'document_number', headerName: 'Number', flex: 1 },
    { field: 'document_name', headerName: 'Name', flex: 2 },
    { field: 'document_location', headerName: 'Location', flex: 1 },
    {
      field: 'pending_changes',
      headerName: 'Pending Changes',
      flex: 2,
      renderCell: params => (params.row?.pending_changes || []).join(', '),
      valueGetter: params => (params.row?.pending_changes || []).join(', ')
    },
    {
      field: 'submitted_changes',
      headerName: 'Submitted Changes',
      flex: 2,
      renderCell: params => (params.row?.submitted_changes || []).join(', '),
      valueGetter: params => (params.row?.submitted_changes || []).join(', ')
    }
  ];

  const renderDocumentLegend = ({ payload }) => (
    <Box sx={{ ml: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Documents</Typography>
      {payload.map((entry, idx) => (
        <Typography key={`item-${idx}`} variant="body2">
          <span style={{ color: entry.color, marginRight: 4 }}>&#9632;</span>
          {`${entry.value}: ${entry.payload.value}`}
        </Typography>
      ))}
      <Typography variant="body2">{`Total: ${documentTotal}`}</Typography>
    </Box>
  );

  const renderImpactLegend = ({ payload }) => (
    <Box sx={{ ml: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Design Change</Typography>
      {payload.map((entry, idx) => (
        <Typography key={`impact-${idx}`} variant="body2">
          <span style={{ color: entry.color, marginRight: 4 }}>&#9632;</span>
          {`${entry.value}: ${entry.payload.value}`}
        </Typography>
      ))}
    </Box>
  );

  const exportToCsv = (rows, columns, fileName) => {
    const header = columns.map(c => c.headerName).join(',');
    const csvRows = rows.map(row =>
      columns
        .map(col => {
          let val = row[col.field];
          if (Array.isArray(val)) {
            val = val.join(' | ');
          }
          if (val === undefined || val === null) {
            val = '';
          }
          return `"${String(val).replace(/"/g, '""')}"`;
        })
        .join(',')
    );
    const csvContent = [header, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const startExport = expFunc => {
    setPendingExport(() => expFunc);
    setConfirmThirdParty(true);
  };

  const handleThirdPartyChoice = (isThird: boolean) => {
    setConfirmThirdParty(false);
    if (!isThird) {
      if (pendingExport && typeof pendingExport === 'function') pendingExport();
      setPendingExport(null);
    } else {
      setMappingOpen(true);
    }
  };

  const handleMappingChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setMapping(prev => ({ ...prev, [field]: e.target.value }));
  };

  const exportMappedRows = (rows: any[]) => {
    if (!rows || !rows.length) return;
    const cols = Object.keys(rows[0]).map(k => ({ field: k, headerName: k }));
    exportToCsv(rows, cols, 'mapped_documents');
  };

  const handleMappingSubmit = () => {
    if (!mapFile) { alert('Select a file'); return; }
    const fd = new FormData();
    fd.append('file', mapFile);
    fd.append('mapping', JSON.stringify(mapping));
    if (dateFormat) fd.append('date_format', dateFormat);
    fetch(apiUrl('/api/documents/export-map'), { method: 'POST', body: fd })
      .then(res => res.json())
      .then(data => {
        exportMappedRows(data);
        setMappingOpen(false);
        setMapFile(null);
      })
      .catch(() => alert('Failed to map file'));
  };

  const handleExportDesign = () => {
    startExport(() => exportToCsv(filteredDesign, designCols, 'design_changes'));
  };

  const handleExportDocuments = () => {
    startExport(() => exportToCsv(filteredDocuments, documentCols, 'documents'));
  };

  if (!projectId) {
    return (
      <EmptyState
        icon="📊"
        title="No Project Selected"
        message="Please select a project from the sidebar to view the dashboard and access project data."
        tip="Use the 'Active Project' dropdown in the sidebar to choose a project"
      />
    );
  }

  return (
    <>
    <Box sx={{ p: { xs: 2, sm: 2, md: 3 }, maxWidth: '100%' }}>

      {/* Design Changes Section */}
      <Card sx={{ mb: 4, boxShadow: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              Design Changes
            </Typography>
            <Button variant="contained" size="small" onClick={handleExportDesign}>
              Export to Excel
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Filter Controls */}
            {/* @ts-ignore */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    multiple
                    value={statusFilter}
                    label="Status Filter"
                    onChange={e => setStatusFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    renderValue={selected => (selected).join(', ')}
                  >
                    {['Completed', 'Pending', 'NA'].map(status => (
                      <MenuItem key={status} value={status}>
                        <Checkbox checked={statusFilter.indexOf(status) > -1} />
                        <ListItemText primary={status} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearDesignFilter}
                  sx={{ minWidth: 'auto' }}
                >
                  <FilterAltOffIcon />
                </Button>
              </Box>
            </Grid>

            {/* Chart */}
            {/* @ts-ignore */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                  <Box sx={{ width: 200, height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={designChartData}
                          dataKey="value"
                          nameKey="name"
                          outerRadius={70}
                          onClick={(data, index) => handleDesignPieClick(data.payload)}
                          style={{ cursor: 'pointer' }}
                        >
                          {designChartData.map((entry, idx) => (
                            <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  {renderDesignLegend({ payload: designLegendData })}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* DataGrid */}
          <Box sx={{ height: { xs: 400, sm: 450, md: 500 }, width: '100%' }}>
            <DataGrid
              rows={filteredDesign.map(d => ({ id: d.change_number, ...d }))}
              columns={designCols}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } }
              }}
              pageSizeOptions={[10, 25, 50]}
              slots={{ toolbar: GridToolbar }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Documents Section */}
      <Card sx={{ mb: 4, boxShadow: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              Documents
            </Typography>
            <Button variant="contained" size="small" onClick={handleExportDocuments}>
              Export to Excel
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Filter Controls */}
            {/* @ts-ignore */}
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      multiple
                      value={assignStatusFilter}
                      label="Status"
                      onChange={e => setAssignStatusFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      renderValue={selected => selected.join(', ')}
                    >
                      {statusOptions.map(status => (
                        <MenuItem key={status} value={status}>
                          <Checkbox checked={assignStatusFilter.indexOf(status) > -1} />
                          <ListItemText primary={status} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" size="small" onClick={clearAssignFilter} sx={{ minWidth: 'auto' }}>
                    <FilterAltOffIcon />
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Autocomplete
                    multiple
                    options={designNumbers}
                    filterOptions={designNumFilter}
                    value={changeFilter}
                    disableCloseOnSelect
                    onChange={(_, val) => setChangeFilter(val)}
                    fullWidth
                    size="small"
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        {option}
                      </li>
                    )}
                    renderInput={params => <TextField {...params} label="Change #" />}
                  />
                  <Button variant="outlined" size="small" onClick={clearChangeFilter} sx={{ minWidth: 'auto' }}>
                    <FilterAltOffIcon />
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Location</InputLabel>
                    <Select
                      multiple
                      value={locationFilter}
                      label="Location"
                      onChange={e => setLocationFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      renderValue={selected => selected.join(', ')}
                    >
                      {locations.map((loc, idx) => (
                        <MenuItem key={idx} value={loc}>
                          <Checkbox checked={locationFilter.indexOf(loc) > -1} />
                          <ListItemText primary={loc} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" size="small" onClick={clearLocationFilter} sx={{ minWidth: 'auto' }}>
                    <FilterAltOffIcon />
                  </Button>
                </Box>

                <Button variant="outlined" fullWidth onClick={clearAllDocumentFilters} startIcon={<FilterAltOffIcon />}>
                  Clear All Filters
                </Button>
              </Stack>
            </Grid>

            {/* Charts */}
            {/* @ts-ignore */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  {/* @ts-ignore */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Status Distribution
                      </Typography>
                      <Box sx={{ height: 180 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={documentChartData}
                              dataKey="value"
                              nameKey="name"
                              outerRadius={60}
                              onClick={(data, idx) =>
                                setAssignStatusFilter(prev =>
                                  prev.includes(data.name)
                                    ? prev.filter(s => s !== data.name)
                                    : [...prev, data.name]
                                )}
                              style={{ cursor: 'pointer' }}
                            >
                              {documentChartData.map((entry, idx) => (
                                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      {renderDocumentLegend({ payload: documentLegendData })}
                    </Box>
                  </Grid>

                  {/* @ts-ignore */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Impact Distribution
                      </Typography>
                      <Box sx={{ height: 180 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={impactChartData}
                              dataKey="value"
                              nameKey="name"
                              outerRadius={60}
                              onClick={(data, idx) => handleImpactPieClick(data.payload)}
                              style={{ cursor: 'pointer' }}
                            >
                              {impactChartData.map((entry, idx) => (
                                <Cell key={idx} fill={IMPACT_COLORS[idx % IMPACT_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      {renderImpactLegend({ payload: impactLegendData })}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* DataGrid */}
          <Box sx={{ height: { xs: 400, sm: 450, md: 500 }, width: '100%' }}>
            <DataGrid
              rows={filteredDocuments.map(d => ({ id: d.document_number, ...d }))}
              columns={documentCols}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } }
              }}
              pageSizeOptions={[10, 25, 50]}
              slots={{ toolbar: GridToolbar }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
    <Dialog open={confirmThirdParty} onClose={() => handleThirdPartyChoice(false)}>
      <DialogTitle>Third-Party Export</DialogTitle>
      <DialogContent>
        <Typography>Is this an export from a third-party source?</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleThirdPartyChoice(false)}>No</Button>
        <Button variant="contained" onClick={() => handleThirdPartyChoice(true)}>Yes</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={mappingOpen} onClose={() => setMappingOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Column Mapping</DialogTitle>
      <DialogContent>
        <input type="file" accept=".xlsx" onChange={e => setMapFile(e.target.files[0])} style={{ marginBottom: 16 }} />
        {mappingFields.map(field => (
          <TextField
            key={field}
            label={field.replace(/_/g, ' ')}
            value={mapping[field]}
            onChange={handleMappingChange(field)}
            fullWidth
            margin="dense"
          />
        ))}
        <TextField
          select
          label="Date Format"
          value={dateFormat}
          onChange={e => setDateFormat(e.target.value)}
          fullWidth
          margin="dense"
        >
          {['dd.mm.yyyy','mm/dd/yyyy','yyyy-mm-dd','dd/mm/yyyy'].map(fmt => (
            <MenuItem key={fmt} value={fmt}>{fmt}</MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleMappingSubmit}>Export</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default Dashboard;
